import { apiConstants, get, post } from '../../common/constants'
import { getAccess } from '../../helpers/ReduxHelpers'

/** The signed-in user's access modules (from the token); [] when unreadable. */
export const userAccessList = () => {
  try { return getAccess() || [] } catch (e) { return [] }
}

/**
 * Weight v2 admin — every backend call the module makes, in one file.
 *
 * Endpoints are the proship_mongo weightv2 controllers (WeightV2Controller,
 * WeightFreezeV2Controller) plus two pre-existing admin endpoints: the v1
 * decision endpoint (tools/weight_discrepancy/update — the ONLY thing that
 * moves a dispute's status and money) and the merchant-config TAT setter.
 */

// Drops null / '' / undefined so they never reach the query string as "null".
const compact = (params = {}) => Object.fromEntries(
  Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '')
)

/** The server's own sentence out of an axios failure, else a fallback. */
export function serverMessage(err, fallback = 'Request failed') {
  const data = err && err.response && err.response.data
  if (data) {
    if (typeof data === 'string' && data) return data
    if (data.error) return data.error
    if (data.message) return data.message
    if (Array.isArray(data.errors) && data.errors.length) return data.errors[0]
  }
  return (err && err.message) || fallback
}

// ── Disputes ─────────────────────────────────────────────────────────────────

/** WdV2ListRequest → { count, remaining, data: WdV2Row[], tabCounts }. Admin without merchantId = every merchant. */
export const wdList = (body) => post(apiConstants.WD_V2_LIST, body).then((r) => r.data)
// Every row the list body matches (not one page), for the queue's download. remaining = 1 means capped.
export const wdExport = (body) => post(`${apiConstants.WD_V2_BASE}/export`, body).then((r) => r.data)

/** Rows per request and the most rows in one download (the server enforces both: 5,000 and 25,000). */
export const EXPORT_SLICE = 5000
export const EXPORT_MAX = 25000

/**
 * The whole download, a slice at a time (28 Oct): each request stays short however large the file, which a
 * single 25,000-row request would not. Rows are de-duplicated by id. Resolves { rows, capped }; capped is true
 * when there were more than EXPORT_MAX rows. onProgress(rowsSoFar) lets the screen show how far it has got.
 */
export async function wdExportAll(body, onProgress) {
  const seen = new Set()
  const rows = []
  for (let from = 0; from < EXPORT_MAX; from += EXPORT_SLICE) {
    const res = await wdExport({ ...body, exportFrom: from, exportLimit: EXPORT_SLICE })
    const data = (res && res.data) || []
    data.forEach((r) => { if (!seen.has(r.id)) { seen.add(r.id); rows.push(r) } })
    if (onProgress) onProgress(rows.length)
    if (!res || !res.remaining) return { rows, capped: false }
  }
  return { rows, capped: true }
}

/**
 * Bulk action file → the existing finance bulk endpoint (FinanceController /wd/bulkAction). It answers
 * HTTP 200 with { status, errorMsg } even when it refuses a file, so a refusal is turned into an error
 * carrying the server's sentence (which names the bad rows).
 */
export const wdBulkUpload = (file, remark) => {
  const form = new FormData()
  form.append('file', file)
  form.append('remark', remark)
  return post(apiConstants.UPLOAD_BULK_ACTION, form).then((r) => {
    const data = r && r.data
    // The row errors arrive joined as " | Row Number 2 - … | Row Number 5 - …": drop the leading separator.
    if (!data || data.status !== 'SUCCESS') throw new Error(String((data && data.errorMsg) || 'Upload failed').replace(/^\s*\|\s*/, ''))
    return data.responseObj
  })
}

/** The last 7 days of bulk files with every row's result (FinanceController /wd/bulkActionStatus). */
export const wdBulkUploads = () => get(apiConstants.GET_BULK_ACTION_WD).then((r) => {
  const data = r && r.data
  if (!data || data.status !== 'SUCCESS') throw new Error((data && data.errorMsg) || 'Could not load recent uploads')
  return Array.isArray(data.responseObj) ? data.responseObj : []
})

export const wdSummary = (params) => get(apiConstants.WD_V2_SUMMARY, compact(params)).then((r) => r.data)

export const wdGet = (id) => get(`${apiConstants.WD_V2_BASE}/${id}`).then((r) => r.data)

/**
 * The decision. Same endpoint and body the v1 screen posts; the server answers
 * per row with { awbNumber, status, actionResult, reason }. A refused action is
 * NOT an HTTP error, so callers must read actionResult.
 */
export const wdDecide = (awbNumber, status, remark) =>
  post(apiConstants.WEIGHT_DISCREPANCY_UPDATE, [{ awbNumber, status, remark }])
    .then((r) => (Array.isArray(r.data) ? r.data[0] : r.data))

/** Item 3 — what settling at weightGm would charge and release; read-only. 400 carries the guard-rail sentence. */
export const wdSettlePreview = (id, weightGm) =>
  get(`${apiConstants.WD_V2_BASE}/${id}/settle-preview`, { weightGm }).then((r) => r.data)

/** Item 3 — settle at a revised weight. The server derives the money from the merchant's rate card; there is no amount here on purpose. */
export const wdSettle = (id, weightGm, remark) =>
  post(`${apiConstants.WD_V2_BASE}/${id}/settle`, { settledWeightGm: weightGm, remark }).then((r) => r.data)

// ── Freeze requests ──────────────────────────────────────────────────────────

/** status may be a comma list of Status names or 'open'; no merchantId = the cross-merchant queue. */
export const wfList = (params) => get(apiConstants.WF_V2_REQUESTS, compact(params)).then((r) => r.data)

export const wfCounts = (params) => get(`${apiConstants.WF_V2_REQUESTS}/counts`, compact(params)).then((r) => r.data)

export const wfGet = (id) => get(`${apiConstants.WF_V2_REQUESTS}/${id}`).then((r) => r.data)

/** The courier freeze in force today for what a request covers (whole-merchant lock and per-SKU freezes). */
export const wfCourierFreeze = (id) => get(`${apiConstants.WF_V2_REQUESTS}/${id}/courier-freeze`).then((r) => r.data)

export const wfRequestInfo = (id, requestedItems, remark) =>
  post(`${apiConstants.WF_V2_REQUESTS}/${id}/request-info`, { requestedItems, remark }).then((r) => r.data)

export const wfApprove = (id, remark) =>
  post(`${apiConstants.WF_V2_REQUESTS}/${id}/approve`, { remark }).then((r) => r.data)

export const wfReject = (id, reason, remark) =>
  post(`${apiConstants.WF_V2_REQUESTS}/${id}/reject`, { reason, remark }).then((r) => r.data)

/** Prozo takes its own auto-proposal off the table (a merchant's request is withdrawn by the merchant). */
export const wfWithdraw = (id, remark) =>
  post(`${apiConstants.WF_V2_REQUESTS}/${id}/withdraw`, { remark }).then((r) => r.data)

/** Item 6 — the merchant's categories as typed, the blanks, the near-duplicate cleanup list. */
export const wfCategoryReport = (merchantId) =>
  get(`${apiConstants.WF_V2_BASE}/category-report`, compact({ merchantId })).then((r) => r.data)

/** T12/T13 — the auto-proposal engine, run now. dryRun lists candidates and writes nothing. */
export const wfAutoPropose = (dryRun, merchantId) =>
  post(`${apiConstants.WD_V2_BASE}/admin/jobs/auto-propose-freezes`, null, { params: compact({ dryRun, merchantId }) })
    .then((r) => r.data)

// ── Per-merchant dispute TAT ─────────────────────────────────────────────────

export const merchantConfig = (merchantId) =>
  get(`${apiConstants.MERCHANT_CONFIG_BASE}/${merchantId}`).then((r) => r.data)

/** days 1..15; null clears the override so the merchant falls back to the platform default (7). */
export const setWdAutoClosureDays = (merchantId, days) =>
  post(`${apiConstants.MERCHANT_CONFIG_BASE}/${merchantId}/wdAutoClosureDays`, null, {
    params: days == null ? {} : { days },
  }).then((r) => r.data)
